/**
 * src/components/games/SnakeActivity.tsx
 *
 * Voice-channel multiplayer Snake. v1 scope:
 *   - Per-player snake; each player controls one snake.
 *   - Host-authoritative game tick (~8 Hz). The host broadcasts the full
 *     state (players, foods, walls, tick number) via NetworkManager's
 *     `state-sync` event. Peers render the resulting state.
 *   - Per-player direction input is sent via NetworkManager.sendInput with
 *     the optional `direction` field on PlayerInput (added in this turn).
 *   - Death is detected client-side per the player's own snake; the host
 *     picks it up on the next tick (alive=false) and the rest of the field
 *     renders a ghost.
 *   - Host-initiated restart re-broadcasts fresh spawn coordinates.
 *
 * Out of v1 scope (deferred to v2):
 *   - Powerups, walls, portals, combos, themes, audio, particle FX.
 *   - Persistent leaderboards (local scores only for now).
 *   - Network reconnection / mid-game player drop handling.
 *
 * Self-registers with GamesRegistry on module load. The framework's
 * buildGameEntries() filters out the LEGACY `snake` IframeGame stub once
 * this manifest lands, so the registry supersedes the iframe automatically.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Gamepad2,
  Wifi,
  WifiOff,
  X,
  RefreshCw,
  Crown,
  Trophy,
  Skull,
  Users,
} from 'lucide-react';
import type { GameRuntimeProps } from '../../game/framework/types';
import { registerGame } from '../../game/framework/GamesRegistry';
import { NetworkManager } from '../../game/NetworkManager';

// ============================================================================
// Sync shape — host broadcasts to peers via state-sync.payload.snakeState
// ============================================================================

type Direction = 'up' | 'down' | 'left' | 'right';
type SnakeStatus = 'lobby' | 'in_progress' | 'completed';

interface SyncFood {
  x: number;
  y: number;
  type: 'NORMAL';
}

interface SyncPlayer {
  userId: string;
  /**
   * socketId is the key under which this player is registered in
   * `session.players` and matches `data.id` in the player's player-update
   * NetworkManager event. Stored separately from userId so the host's
   * tick loop can match incoming peer input events to the correct
   * SnakeSyncState row without server-side key rewriting.
   */
  socketId: string;
  displayName: string;
  color: string;
  snake: Array<{ x: number; y: number }>;
  direction: Direction;
  pendingDirection: Direction | null; // queued turn applied on next tick
  alive: boolean;
  score: number;
}

interface SnakeSyncState {
  tick: number;
  gridSize: number;
  foods: SyncFood[];
  players: SyncPlayer[];
  status: SnakeStatus;
  hostUserId: string;
}

// ============================================================================
// Constants
// ============================================================================

const GRID = 20;
const CANVAS_PX = 480;
const CELL_PX = CANVAS_PX / GRID;
const TICK_MS = 130;
const PLAYER_COLORS = [
  '#00ffe1', // cyan
  '#ff0099', // pink
  '#ffd000', // gold
  '#9b59ff', // violet
  '#39ff14', // lime
  '#ff7a00', // orange
  '#00bfff', // sky
  '#ff3366', // crimson
];

// Default directions per slot index — spreads new snakes apart so first-frame
// collisions are unlikely when 2-4 peers join simultaneously.
const SLOT_DIRECTIONS: Direction[] = ['right', 'left', 'down', 'up', 'right', 'left', 'down', 'up'];

// ============================================================================
// Pure helpers (unit-testable, no React state)
// ============================================================================

function seedSnakeFor(idx: number): Array<{ x: number; y: number }> {
  // Anchor each spawn row at a different quadrant so four concurrent players
  // never start on top of each other. Length 3 facing the slot's natural
  // direction so the first tick doesn't immediately self-collide.
  const anchors = [
    { x: 4, y: 4 },
    { x: GRID - 5, y: 4 },
    { x: 4, y: GRID - 5 },
    { x: GRID - 5, y: GRID - 5 },
    { x: Math.floor(GRID / 2), y: 4 },
    { x: Math.floor(GRID / 2), y: GRID - 5 },
    { x: 4, y: Math.floor(GRID / 2) },
    { x: GRID - 5, y: Math.floor(GRID / 2) },
  ];
  const a = anchors[idx % anchors.length];
  const dir = SLOT_DIRECTIONS[idx % SLOT_DIRECTIONS.length];
  // Walk backwards 2 cells so the head points in `dir`.
  const segs: Array<{ x: number; y: number }> = [];
  for (let i = 0; i < 3; i++) {
    segs.push({ x: a.x - dirVec(dir).x * i, y: a.y - dirVec(dir).y * i });
  }
  return segs;
}

function dirVec(d: Direction): { x: number; y: number } {
  switch (d) {
    case 'up':
      return { x: 0, y: -1 };
    case 'down':
      return { x: 0, y: 1 };
    case 'left':
      return { x: -1, y: 0 };
    case 'right':
      return { x: 1, y: 0 };
  }
}

function isOpposite(a: Direction, b: Direction): boolean {
  return dirVec(a).x === -dirVec(b).x && dirVec(a).y === -dirVec(b).y;
}

function spawnFood(players: SyncPlayer[], existing: SyncFood[]): SyncFood {
  for (let attempts = 0; attempts < 200; attempts++) {
    const x = Math.floor(Math.random() * GRID);
    const y = Math.floor(Math.random() * GRID);
    const onSnake = players.some((p) =>
      p.snake.some((s) => s.x === x && s.y === y)
    );
    const onOtherFood = existing.some((f) => f.x === x && f.y === y);
    if (!onSnake && !onOtherFood) return { x, y, type: 'NORMAL' };
  }
  // Last-resort fallback: centre cell, overwrite any prev food.
  return { x: Math.floor(GRID / 2), y: Math.floor(GRID / 2), type: 'NORMAL' };
}

function stepSnake(state: SnakeSyncState): SnakeSyncState {
  // Apply any queued `pendingDirection` per player (set via sendInput), then
  // move each alive snake one cell forward and resolve collisions / food.
  let foods = state.foods;
  const players = state.players.map((p): SyncPlayer => {
    if (!p.alive) return p;

    // Resolve pending turn — only accept non-180 flips so a last-second
    // mirror key can't kill the player mid-tick.
    const nextDir =
      p.pendingDirection && !isOpposite(p.pendingDirection, p.direction)
        ? p.pendingDirection
        : p.direction;

    const v = dirVec(nextDir);
    const head = p.snake[0];
    const nx = head.x + v.x;
    const ny = head.y + v.y;

    // Wall/boundary collision.
    if (nx < 0 || nx >= GRID || ny < 0 || ny >= GRID) {
      return { ...p, alive: false, direction: nextDir, pendingDirection: null };
    }
    // Self collision (skip the tail since it moves out of the way UNLESS we eat).
    if (p.snake.some((s, i) => i > 0 && s.x === nx && s.y === ny)) {
      return { ...p, alive: false, direction: nextDir, pendingDirection: null };
    }

    const newSnake = [{ x: nx, y: ny }, ...p.snake];

    // Other-snake collision: a head entering another snake's body kills us.
    for (const other of state.players) {
      if (other.userId === p.userId) continue;
      if (other.snake.some((s) => s.x === nx && s.y === ny)) {
        return { ...p, alive: false, direction: nextDir, pendingDirection: null };
      }
    }

    // Food collision: grow + score, keep tail.
    const ateIdx = foods.findIndex((f) => f.x === nx && f.y === ny);
    if (ateIdx >= 0) {
      foods = [...foods.slice(0, ateIdx), ...foods.slice(ateIdx + 1)];
      return {
        ...p,
        snake: newSnake,
        score: p.score + 10,
        direction: nextDir,
        pendingDirection: null,
        alive: true,
      };
    }
    newSnake.pop();
    return { ...p, snake: newSnake, direction: nextDir, pendingDirection: null, alive: true };
  });

  // Refill at least one food if the board is empty.
  if (foods.length === 0) foods = [spawnFood(players, [])];

  return { ...state, tick: state.tick + 1, players, foods };
}

// ============================================================================
// SnakeActivity component
// ============================================================================

export default function SnakeActivity(props: GameRuntimeProps) {
  const { session, playerSelf, onExit, onChange, notify, initialRoomId } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const networkRef = useRef<NetworkManager | null>(null);
  const isHostRef = useRef<boolean>(false);
  // Pending direction queued by THIS peer between ticks. The host picks
  // it up on the next tick and broadcasts the result back via state-sync.
  const myPendingDirectionRef = useRef<Direction | null>(null);
  /**
   * Per-peer pending direction, populated by onPlayerUpdate. The host's
   * tick loop folds these in alongside its own `myPendingDirectionRef` so
   * every player — not just the host — can steer their snake in a
   * multiplayer voice channel. Keyed by socket id so it's a 1:1 match
   * with the key under `session.players`.
   */
  const peerDirectionsRef = useRef<Map<string, Direction>>(new Map());

  const [syncState, setSyncState] = useState<SnakeSyncState | null>(null);
  const [isHost, setIsHost] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected'>(
    'connecting'
  );

  const myUserId: string | null = playerSelf?.userId ?? null;

  // --------------------------------------------------------------------------
  // Lifecycle ping (mount → 'snake', unmount → null) so the host voice-channel
  // 🎮 pill lights up reliably on every enter path.
  // --------------------------------------------------------------------------
  useEffect(() => {
    onChange?.('snake');
    return () => onChange?.(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --------------------------------------------------------------------------
  // NetworkManager lifecycle. The NetworkManager emits 'join-vault' on
  // connect and the server replies with 'host-assignment'. We re-mount it
  // when session.id changes (voice-channel move).
  // --------------------------------------------------------------------------
  useEffect(() => {
    // session?.id is always undefined in practice — nothing in the
    // codebase ever constructs a real GameSession (see the
    // GameRuntimeProps comment on initialRoomId) — so fall back to the
    // voice-channel id GameManager is mounted with. This restores the
    // join-vault/host-assignment handshake; it does NOT restore
    // session.players (the seeding effect further down still has no real
    // roster source and continues to degrade gracefully — it stays
    // no-op'd rather than fabricating fake players).
    const roomId = session?.id ?? initialRoomId;
    if (!roomId) return;

    const nm = new NetworkManager(roomId, 'snake');
    networkRef.current = nm;

    nm.onHostAssignment = (assignedHost) => {
      isHostRef.current = assignedHost;
      setIsHost(assignedHost);
      setConnectionStatus('connected');
    };

    nm.onStateSync = (raw) => {
      // server-side state-sync → we read snakeState out of it.
      const snakeState = (raw as any).snakeState as SnakeSyncState | undefined;
      if (snakeState && typeof snakeState === 'object') {
        setSyncState(snakeState);
        setConnectionStatus('connected');
      }
    };

    nm.onPlayerUpdate = (data) => {
      // data.id is the socket id assigned by the server; data.direction is
      // the peer's most recent steer input (added on PlayerInput this turn
      // and mirrored on PlayerState so the host can read it here). The
      // host's tick loop looks up this Map by socket id and folds the
      // value into the peer's pendingDirection on the next tick.
      if (data && data.direction) {
        peerDirectionsRef.current.set(data.id, data.direction);
      }
    };

    return () => {
      nm.disconnect();
      networkRef.current = null;
      isHostRef.current = false;
      setIsHost(false);
      setSyncState(null);
      setConnectionStatus('disconnected');
    };
  }, [session?.id, initialRoomId]);

  // --------------------------------------------------------------------------
  // Per-peer → host input: on every peer keypress, queue the direction in
  // a ref and emit via NetworkManager.sendInput. The host applies
  // myPendingDirectionRef for THIS peer on each tick.
  // --------------------------------------------------------------------------
  const queueDirection = useCallback(
    (dir: Direction) => {
      myPendingDirectionRef.current = dir;
      // Emit to the host so others stay in sync even if our local state
      // diverges from the broadcast — server is the tie-breaker.
      networkRef.current?.sendInput({
        x: 0,
        face: dir,
        isShooting: false,
        direction: dir,
      });
    },
    []
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      // Pick the direction; reject 180-degree flips against my CURRENT
      // last-applied direction (snake can never reverse mid-body). Without
      // this, a player can queue a flip that snaps them into themselves on
      // the next tick.
      const cur = myPendingDirectionRef.current ?? syncState?.players.find(
        (p) => p.userId === myUserId
      )?.direction;
      let next: Direction | null = null;
      switch (e.key) {
        case 'ArrowUp':
        case 'w':
        case 'W':
          next = 'up';
          break;
        case 'ArrowDown':
        case 's':
        case 'S':
          next = 'down';
          break;
        case 'ArrowLeft':
        case 'a':
        case 'A':
          next = 'left';
          break;
        case 'ArrowRight':
        case 'd':
        case 'D':
          next = 'right';
          break;
      }
      if (!next) return;
      if (cur && isOpposite(next, cur)) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      myPendingDirectionRef.current = next;
      networkRef.current?.sendInput({
        x: 0,
        face: next,
        isShooting: false,
        direction: next,
      });
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [myUserId, syncState]);

  // --------------------------------------------------------------------------
  // Host tick + state broadcast. Every TICK_MS, step the snake one cell and
  // then push the new state via sendState so peers re-render.
  //
  // Also folds in any peer `sendInput` (queue in myPendingDirectionRef per
  // user). v1 simplification: pendingDirection is set on the local
  // SnakeSyncState directly when this peer presses a key; other peers'
  // inputs arrive via the socket 'player-input' event which on the server
  // updates their stored direction — but v1 doesn't have that server-side
  // glue. TODO v2: server-side per-player input folding. For v1 the host
  // treats ONLY its own pending direction as authoritative.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isHost) return;

    const id = window.setInterval(() => {
      setSyncState((prev) => {
        if (!prev) return prev;
        // Fold THIS host's pending direction AND every peer's pending
        // direction into the corresponding player's pendingDirection slot,
        // then step forward. stepSnake consumes and clears each consumed
        // pendingDirection so a direction change fires exactly once per
        // tick (and is not re-applied on subsequent ticks until the
        // player steers again).
        const peerMap = peerDirectionsRef.current;
        const myPending = myPendingDirectionRef.current;
        const players = prev.players.map((p): SyncPlayer => {
          let pending: Direction | null = null;
          if (p.userId === myUserId && myPending) {
            pending = myPending;
          } else if (peerMap.has(p.socketId)) {
            pending = peerMap.get(p.socketId) ?? null;
          }
          if (!pending) return p;
          // Clear consumed values post-fold so the same pending direction
          // doesn't re-fire every tick.
          if (p.userId === myUserId) myPendingDirectionRef.current = null;
          else peerMap.delete(p.socketId);
          return { ...p, pendingDirection: pending };
        });
        const advanced: SnakeSyncState = { ...prev, players };
        const next = stepSnake(advanced);
        // Broadcast to peers. Snake state piggybacks on
        // state-sync.payload.snakeState.
        const nm = networkRef.current;
        if (nm) {
          // GameStateSync.snakeState?: unknown accepts `next: SnakeSyncState`
          // directly — no `as any` cast required (it was needed when the
          // interface carried only the UwU Invaders fields; the unknown
          // slot added this turn is the explicit landing pad).
          nm.sendState({
            aliens: [],
            bullets: [],
            score: 0,
            lives: 0,
            level: 0,
            wave: 0,
            state: 'snake',
            snakeState: next,
          });
        }
        return next;
      });
    }, TICK_MS);

    return () => clearInterval(id);
  }, [isHost, myUserId]);

  // --------------------------------------------------------------------------
  // Host bootstrap: when the host connects and no syncState has arrived yet,
  // seed an initial SnakeSyncState based on the session's roster.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!isHost || syncState || !session) return;
    if (!session.players || Object.keys(session.players).length === 0) return;
    // Iterate entries (not values) so we keep the socketId key — that's
    // what NetworkManager.onPlayerUpdate reports as `data.id` and what we
    // use to match a peer's input back to their SnakeSyncState row.
    const players: SyncPlayer[] = Object.entries(session.players).map(([socketId, p], i) => ({
      socketId,
      userId: p.userId,
      displayName: (p as any).displayName ?? (p as any).name ?? p.userId.slice(0, 5),
      color: PLAYER_COLORS[i % PLAYER_COLORS.length],
      snake: seedSnakeFor(i),
      direction: SLOT_DIRECTIONS[i % SLOT_DIRECTIONS.length],
      pendingDirection: null,
      alive: true,
      score: 0,
    }));
    const initial: SnakeSyncState = {
      tick: 0,
      gridSize: GRID,
      foods: Array.from({ length: 3 }).map(() => spawnFood(players, [])),
      players,
      status: 'in_progress',
      hostUserId: session.hostUserId,
    };
    setSyncState(initial);
    networkRef.current?.sendState({
      aliens: [],
      bullets: [],
      score: 0,
      lives: 0,
      level: 0,
      wave: 0,
      state: 'snake',
      snakeState: initial,
    });
  }, [isHost, syncState, session]);

  // --------------------------------------------------------------------------
  // Canvas paint loop. Runs via requestAnimationFrame to keep the visual
  // smooth at 60fps while the underlying tick is throttled to TICK_MS.
  // --------------------------------------------------------------------------
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    // Background
    ctx.fillStyle = '#040711';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Grid lines
    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    for (let i = 1; i < GRID; i++) {
      ctx.beginPath();
      ctx.moveTo(i * CELL_PX, 0);
      ctx.lineTo(i * CELL_PX, canvas.height);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(0, i * CELL_PX);
      ctx.lineTo(canvas.width, i * CELL_PX);
      ctx.stroke();
    }
    if (!syncState) return;
    // Food
    for (const f of syncState.foods) {
      ctx.fillStyle = '#ff0055';
      ctx.shadowBlur = 14;
      ctx.shadowColor = '#ff0055';
      ctx.fillRect(f.x * CELL_PX + 3, f.y * CELL_PX + 3, CELL_PX - 6, CELL_PX - 6);
      ctx.shadowBlur = 0;
    }
    // Players
    for (const p of syncState.players) {
      ctx.fillStyle = p.color;
      p.snake.forEach((seg, i) => {
        const isHead = i === 0;
        ctx.globalAlpha = isHead ? 1 : 0.65;
        ctx.fillRect(seg.x * CELL_PX + 2, seg.y * CELL_PX + 2, CELL_PX - 4, CELL_PX - 4);
        if (isHead) {
          ctx.globalAlpha = 0.35;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(seg.x * CELL_PX + 4, seg.y * CELL_PX + 4, CELL_PX - 8, CELL_PX - 8);
          ctx.globalAlpha = 1;
          ctx.fillStyle = p.color;
        }
      });
      ctx.globalAlpha = 1;
      if (!p.alive) {
        // Ghost overlay: outlined only.
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 2;
        p.snake.forEach((seg) => {
          ctx.strokeRect(seg.x * CELL_PX + 2, seg.y * CELL_PX + 2, CELL_PX - 4, CELL_PX - 4);
        });
        ctx.lineWidth = 1;
      }
    }
  }, [syncState]);

  useEffect(() => {
    let raf = 0;
    const loop = () => {
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [draw]);

  // --------------------------------------------------------------------------
  // Derived view state
  // --------------------------------------------------------------------------
  const me =
    syncState && myUserId
      ? syncState.players.find((p) => p.userId === myUserId) ?? null
      : null;
  const aliveCount = syncState?.players.filter((p) => p.alive).length ?? 0;
  const totalCount = syncState?.players.length ?? 0;
  const sortedByScore = syncState
    ? [...syncState.players].sort((a, b) => b.score - a.score)
    : [];

  const handleExit = useCallback(() => {
    onChange?.(null);
    onExit();
  }, [onChange, onExit]);

  const handleRestart = useCallback(() => {
    if (!isHost) {
      notify?.('Only the host can restart the round', { kind: 'warn' });
      return;
    }
    if (!syncState || !session) return;
    const players: SyncPlayer[] = syncState.players.map((p, i) => ({
      ...p,
      snake: seedSnakeFor(i),
      direction: SLOT_DIRECTIONS[i % SLOT_DIRECTIONS.length],
      pendingDirection: null,
      alive: true,
      score: 0,
    }));
    const next: SnakeSyncState = {
      ...syncState,
      tick: 0,
      foods: Array.from({ length: 3 }).map(() => spawnFood(players, [])),
      players,
      status: 'in_progress',
    };
    setSyncState(next);
    networkRef.current?.sendState({
      aliens: [],
      bullets: [],
      score: 0,
      lives: 0,
      level: 0,
      wave: 0,
      state: 'snake',
      snakeState: next,
    });
    notify?.('Round restarted', { kind: 'info' });
  }, [isHost, syncState, session, notify]);

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div className="relative w-full h-full bg-[#040711] text-white flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-white/10 bg-gradient-to-r from-black via-black to-[#0a0612]">
        <div className="flex items-center gap-3">
          <Gamepad2 className="text-[var(--neon)]" size={26} />
          <div className="flex flex-col">
            <h1 className="text-lg font-black tracking-tight">
              Cyber Snake <span className="text-[10px] text-white/30 font-mono uppercase ml-1">voice-channel multiplayer</span>
            </h1>
            <div className="text-[10px] text-white/40 font-mono uppercase tracking-widest flex items-center gap-3">
              <span className="flex items-center gap-1">
                {connectionStatus === 'connected' ? (
                  <Wifi size={10} className="text-emerald-400" />
                ) : connectionStatus === 'connecting' ? (
                  <Wifi size={10} className="text-amber-400 animate-pulse" />
                ) : (
                  <WifiOff size={10} className="text-red-400" />
                )}
                {connectionStatus}
              </span>
              {isHost && (
                <span className="flex items-center gap-1 text-[var(--neon)]">
                  <Crown size={10} /> host
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users size={10} /> {aliveCount}/{totalCount} alive
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isHost && syncState && (
            <button
              onClick={handleRestart}
              className="px-3 py-1.5 rounded-xl border border-[var(--neon)]/40 text-[var(--neon)] text-[10px] font-bold uppercase tracking-widest hover:bg-[var(--neon)]/10 transition-colors flex items-center gap-1.5"
              title="Restart Round (host only)"
            >
              <RefreshCw size={11} /> Restart
            </button>
          )}
          <button
            onClick={handleExit}
            className="px-3 py-1.5 rounded-xl border border-red-500/30 text-red-400 text-[10px] font-bold uppercase tracking-widest hover:bg-red-500/15 transition-colors flex items-center gap-1.5"
          >
            <X size={11} /> Exit
          </button>
        </div>
      </div>

      {/* Main: canvas + side panel */}
      <div className="flex-1 flex flex-col md:flex-row min-h-0">
        <div className="flex-1 flex items-center justify-center p-4 bg-[#020308] relative">
          <canvas
            ref={canvasRef}
            width={CANVAS_PX}
            height={CANVAS_PX}
            className="bg-[#040711] border border-white/10 rounded-2xl shadow-[0_0_50px_rgba(0,255,255,0.05)]"
          />
          {!me && syncState && (
            <div className="absolute inset-x-4 top-4 bg-amber-500/10 border border-amber-500/40 rounded-xl px-3 py-2 text-[11px] text-amber-300">
              You haven't been registered in the roster yet. Wait for the host to refresh, or rejoin the voice channel.
            </div>
          )}
        </div>

        {/* Scoreboard */}
        <aside className="w-full md:w-64 bg-black/40 border-l border-white/10 p-4 flex flex-col gap-2 overflow-y-auto">
          <div className="flex items-center gap-2 mb-1">
            <Trophy className="text-yellow-400" size={14} />
            <h2 className="text-[10px] font-black uppercase tracking-widest text-white/60">Scoreboard</h2>
          </div>
          <div className="text-[9px] text-white/40 font-mono uppercase mb-2">tick #{syncState?.tick ?? 0}</div>
          {sortedByScore.map((p, i) => (
            <div
              key={p.userId}
              className={`flex items-center gap-2 p-2 rounded-lg border ${
                p.alive
                  ? 'border-white/10 bg-white/5'
                  : 'border-red-500/20 bg-red-500/5 opacity-60'
              }`}
            >
              <span
                className="inline-block w-3 h-3 rounded-sm shrink-0"
                style={{ background: p.color }}
              />
              <span className="flex-1 min-w-0 text-[11px] font-bold truncate flex items-center gap-1">
                {p.userId === session?.hostUserId && <Crown size={9} className="text-[var(--neon)]" />}
                {p.displayName}
                {!p.alive && <Skull size={10} className="text-red-400" />}
              </span>
              <span className="text-[11px] font-mono font-bold text-white tabular-nums">
                {p.score}
              </span>
            </div>
          ))}
          {sortedByScore.length === 0 && (
            <div className="text-[10px] text-white/30 font-mono uppercase text-center py-4">
              Waiting for host…
            </div>
          )}
        </aside>
      </div>

      {/* Footer hint */}
      <div className="px-4 py-2 border-t border-white/10 text-[10px] text-white/40 font-mono uppercase tracking-widest flex items-center gap-2 bg-black/30">
        <span>WASD / Arrow Keys to steer</span>
        <span className="opacity-30">·</span>
        <span>{isHost ? 'You are the host — Restart resets the round' : 'Host-controlled scoring'}</span>
      </div>
    </div>
  );
}

// ============================================================================
// Self-register: this manifest tells GamesRegistry that 'snake' is now a
// registered multiplayer activity. GameManager.buildGameEntries() filters
// out the LEGACY `id: 'snake'` IframeGame stub because its id now matches
// a registered entry, so the in-app version takes over automatically — no
// GameManager.tsx change required.
// ============================================================================
registerGame({
  id: 'snake',
  displayName: 'Cyber Snake',
  shortDescription: 'Voice-channel multiplayer snake — eat, dodge, survive.',
  multiplayer: true,
  multiplayerShape: {
    maxPlayers: 8,
    transport: 'socket.io',
    requiresVoiceChannel: true,
    hostMigration: 'auto',
  },
  leaderboard: 'score',
  available: true,
  asset: async () => ({ default: SnakeActivity }),
  icon: Gamepad2,
});
